import * as FileSaver from 'file-saver';

/**
 * Impressão pelo NAVEGADOR, a partir de um PDF vindo da API.
 *
 * <p>A impressão pelo servidor só alcança impressora quando a API está na mesma rede — e nem sempre
 * nem isso, porque em container ela não enxerga o CUPS do host. Aqui quem imprime é a máquina que
 * está com a pessoa, na impressora que o sistema operacional já conhece.</p>
 *
 * <p>O PDF é carregado num iframe oculto e impresso de lá. Não abre aba nova de propósito: aba nova
 * é bloqueada por popup blocker e obriga o usuário a fechar depois — num tablet de cozinha, cada
 * toque a mais conta.</p>
 */
/** Onde fica o que se aprendeu sobre a impressão deste aparelho. */
const CHAVE_IMPRESSAO_DIRETA = 'impressao.direta';

/**
 * Acima disto, houve caixa de diálogo — ou seja, o navegador NÃO está em modo de impressão direta.
 * Impressão em quiosque volta em dezenas de milissegundos; um humano não fecha diálogo em 1,2s.
 */
const LIMITE_DIALOGO_MS = 1200;

/**
 * O aparelho imprime direto (Chrome com --kiosk-printing)?
 *
 * @returns `true`/`false` depois da primeira impressão; `null` enquanto nunca se imprimiu por aqui.
 *          Diferenciar "não sei" de "não" evita alarme falso em quem ainda não usou a impressão.
 */
export function impressaoDiretaDetectada(): boolean | null {
  try {
    const valor = localStorage.getItem(CHAVE_IMPRESSAO_DIRETA);
    return valor === null ? null : valor === '1';
  } catch {
    return null;
  }
}

function registrarModoImpressao(direta: boolean): void {
  try {
    localStorage.setItem(CHAVE_IMPRESSAO_DIRETA, direta ? '1' : '0');
  } catch {
    // navegador sem storage: perde-se só a dica, não a impressão
  }
}

export function imprimirPdfNoNavegador(pdf: ArrayBuffer | Blob, aoFalhar?: (motivo: string) => void): void {
  try {
    const blob = pdf instanceof Blob ? pdf : new Blob([pdf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;

    let encerrado = false;
    const encerrar = () => {
      if (encerrado) {
        return;
      }
      encerrado = true;
      // some com o iframe e devolve a memória; sem isto cada impressão deixa um blob para trás.
      setTimeout(() => {
        URL.revokeObjectURL(url);
        iframe.remove();
      }, 60000);
    };

    iframe.onload = () => {
      try {
        const janela = iframe.contentWindow;
        if (!janela) {
          aoFalhar?.('Não foi possível preparar a impressão.');
          encerrar();
          return;
        }
        janela.focus();
        // Mede quanto o print() demora para devolver: com impressão direta ele volta quase
        // imediato; com caixa de diálogo, só volta quando a pessoa confirma. É o que permite
        // avisar na configuração sem perguntar nada a ninguém.
        const inicio = Date.now();
        janela.print();
        registrarModoImpressao(Date.now() - inicio < LIMITE_DIALOGO_MS);
        encerrar();
      } catch (e) {
        aoFalhar?.('O navegador bloqueou a impressão. Abra o PDF e imprima manualmente.');
        encerrar();
      }
    };

    document.body.appendChild(iframe);
  } catch (e) {
    aoFalhar?.('Falha ao preparar o PDF para impressão.');
  }
}

/** Celular e tablet não desenham PDF dentro de iframe: a impressão sairia em branco. */
function navegadorImprimePdfEmbutido(): boolean {
  try {
    const ua = navigator.userAgent || '';
    // iPad moderno se apresenta como Mac; o toque é o que o denuncia.
    const ipad = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
    return !/Android|iPhone|iPad|iPod/i.test(ua) && !ipad;
  } catch {
    return false;
  }
}

/**
 * Impressão dos documentos do lançamento (DANFE e demais formulários, prévia, carta de correção):
 * abre o assistente de impressão na própria janela em vez de baixar o arquivo. Os relatórios ficam
 * de fora de propósito — continuam sendo baixados, para a pessoa abrir e conferir antes.
 *
 * <p>Baixar era o que se fazia antes, e falhava calado na intranet: ela roda em HTTP, o Chrome
 * trata o download como inseguro e, no atalho `--app`, a confirmação fica numa bolha que a janela
 * não mostra. Imprimir daqui não passa por download nenhum.</p>
 *
 * <p>O arquivo ainda é salvo com o nome informado quando não há como imprimir daqui: no celular
 * ou quando o navegador barra a impressão.</p>
 */
export function imprimirOuBaixarPdf(pdf: ArrayBuffer | Blob, nomeArquivo: string): void {
  const blob = pdf instanceof Blob ? pdf : new Blob([pdf], { type: 'application/pdf' });
  if (!navegadorImprimePdfEmbutido()) {
    FileSaver.saveAs(blob, nomeArquivo);
    return;
  }
  imprimirPdfNoNavegador(blob, () => FileSaver.saveAs(blob, nomeArquivo));
}
